// ─────────────────────────────────────────────────────────────────────────────
// Cloud mirror for build saves. The localStorage slots are the source of truth;
// when the user is logged in we push changes to the cloud and, at login, merge
// the two sides (with a single global prompt on divergence).
// ─────────────────────────────────────────────────────────────────────────────

import {
	MAX_SAVE_SLOTS,
	ensureSlots,
	readSlot,
	writeSlot,
	emptySlot,
	readAllSlots,
	stripMeta,
	slotHasData,
	slotComparable,
	cloudToSlot,
} from './buildStorage'

const API = 'https://api.roprime-simulator.com/api/builds'

// Dispatched after the local slots are rewritten by a sync, so the UI refreshes.
export const BUILDS_SYNCED_EVENT = 'builds-synced'

function notifySynced() {
	window.dispatchEvent(new Event(BUILDS_SYNCED_EVENT))
}

// ─── REST client ──────────────────────────────────────────────────────────────

async function cloudGetBuilds(token) {
	const res = await fetch(API, { headers: { Authorization: `Bearer ${token}` } })
	if (!res.ok) throw new Error('Failed to fetch builds')
	const data = await res.json()
	return Array.isArray(data.builds) ? data.builds : []
}

async function cloudPutBuild(token, slot, name, payload) {
	const res = await fetch(`${API}/${slot}`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ name, payload }),
	})
	if (!res.ok) throw new Error('Failed to save build')
}

async function cloudDeleteBuild(token, slot) {
	await fetch(`${API}/${slot}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	}).catch(() => {})
}

// ─── Per-slot push (called by the Save / Rename actions) ──────────────────────

// Mirror one local slot to the cloud: PUT if it holds data, DELETE otherwise.
export async function pushSlotToCloud(token, i) {
	if (!token) return
	const slot = readSlot(i)
	try {
		if (slotHasData(slot)) {
			await cloudPutBuild(token, i, (slot.name || '').trim(), stripMeta(slot))
		} else {
			await cloudDeleteBuild(token, i)
		}
	} catch (e) {
		console.error('builds: cloud push failed for slot', i, e)
	}
}

// ─── Full push / pull ─────────────────────────────────────────────────────────

async function pushAllToCloud(token, local) {
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
		if (slotHasData(local[i])) {
			await cloudPutBuild(token, i, (local[i].name || '').trim(), stripMeta(local[i]))
		} else {
			await cloudDeleteBuild(token, i)
		}
	}
}

function pullAllToLocal(cloudMap) {
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
		const row = cloudMap.get(i)
		writeSlot(i, row ? cloudToSlot(row) : emptySlot())
	}
	notifySynced()
}

// ─── Login merge ──────────────────────────────────────────────────────────────

// Default global conflict resolver. Returns 'cloud' or 'local'.
function defaultResolveConflict({ localDate, cloudDate }) {
	const msg =
		'Your local and cloud saves are different.\n\n' +
		`Local  — last change: ${localDate || 'unknown'}\n` +
		`Cloud  — last change: ${cloudDate || 'unknown'}\n\n` +
		'OK = keep the CLOUD saves (overwrites local)\n' +
		'Cancel = keep the LOCAL saves (overwrites cloud)'
	// eslint-disable-next-line no-alert
	return window.confirm(msg) ? 'cloud' : 'local'
}

function maxDate(dates) {
	return dates.filter(Boolean).sort().slice(-1)[0] || null
}

/**
 * Reconcile localStorage slots with the cloud at login.
 *  - cloud empty, local has data        → push local
 *  - local empty, cloud has data        → pull cloud
 *  - both identical                     → nothing
 *  - both differ                        → one global prompt (keep local | cloud)
 *
 * @param {string} token        bearer token
 * @param {object} [opts]       { resolveConflict?: ({localDate, cloudDate}) => 'cloud'|'local' }
 */
export async function syncBuilds(token, opts = {}) {
	if (!token) return
	ensureSlots()

	let cloudRows
	try {
		cloudRows = await cloudGetBuilds(token)
	} catch (e) {
		console.error('builds sync: cloud fetch failed', e)
		return
	}

	const cloudMap = new Map(cloudRows.map((r) => [r.slot, r]))
	const local = readAllSlots()

	const localCmp = new Map()
	const cloudCmp = new Map()
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
		const s = local[i]
		if (slotHasData(s)) {
			localCmp.set(i, slotComparable({ name: s.name, payload: stripMeta(s) }))
		}
		const r = cloudMap.get(i)
		if (r) {
			cloudCmp.set(i, slotComparable({ name: r.name, payload: r.payload }))
		}
	}

	const localHas = localCmp.size > 0
	const cloudHas = cloudCmp.size > 0

	if (!localHas && !cloudHas) return
	if (cloudHas && !localHas) {
		pullAllToLocal(cloudMap)
		return
	}
	if (localHas && !cloudHas) {
		await pushAllToCloud(token, local)
		return
	}

	// Both sides have data — identical?
	const identical =
		localCmp.size === cloudCmp.size &&
		[...localCmp].every(([k, v]) => cloudCmp.get(k) === v)
	if (identical) return

	// Divergence — single global choice.
	const localDate = maxDate(local.map((s) => s && s.updatedAt))
	const cloudDate = maxDate(cloudRows.map((r) => r.updatedAt))
	const resolve = opts.resolveConflict || defaultResolveConflict
	const choice = await resolve({ localDate, cloudDate })

	if (choice === 'cloud') {
		pullAllToLocal(cloudMap)
	} else {
		await pushAllToCloud(token, local)
	}
}
